import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import arrayMove from 'array-move';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import InputAdornment from '@material-ui/core/InputAdornment';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import { CardHeader } from '@material-ui/core';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import EditOutlined from '@material-ui/icons/EditOutlined';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import CategoryIcon from '@material-ui/icons/Category';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import {
    createSubCollection,
    updateCollectionsService,
    collectionsxIdService,
    orderCollections,
} from '../../../services/collections';
import {
    getSubcategorySortSelectIdFromCollection,
    isSubcategorySortSelectId,
    getSortConfigFromSelectId,
    sortProductsBySelectId,
    buildCollectionUpdatePayload,
    applySavedSortToProducts,
    addProductWithCollectionSort,
    enrichCollectionWithSortPreference,
    persistCollectionSortPreference,
} from '../collectionProductSort';
import SubcategoryProductSortSelect from './SubcategoryProductSortSelect';

import SortableSubCollectionList from './SortableSubCollectionList';
import SortableSubCollectionRow, { SubCollectionDragHandle } from './SortableSubCollectionRow';
import {
    SUBCOLLECTION_SORTABLE_PROPS,
    getSubcollectionHelperDimensions,
    getSubcollectionIds,
    persistSubcollectionOrder,
} from './sortableSubCollectionConfig';

function getProductImageUrl(product) {
    const img = product.product_images && product.product_images.length > 0
        ? product.product_images[0].picture_url
        : product.picture_url;
    if (!img) return null;
    return process.env.REACT_APP_IMAGES_API_URL + 'fit-in/600x600/' + img;
}

const mobileCardStyle = {
    marginTop: 16,
    marginBottom: 16,
    borderRadius: 12,
    border: '1px solid #E8E8E8',
    boxShadow: '0px 1px 3px rgba(0,0,0,0.06)',
    overflow: 'hidden',
};

const SubCollection = ({
    chipDataSubCollection,
    setChipDataSubCollection,
    addHandlerSubCollection,
    handleDelete,
    nameSubCollection,
    listDataProduct,
    setListDataProduct,
    storeId,
    parentId,
    isMobile,
}) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [modalOpen, setModalOpen] = useState(false);
    const [editingSubcategory, setEditingSubcategory] = useState(null);
    const [newSubName, setNewSubName] = useState('');
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [productSearch, setProductSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [loadingEditProducts, setLoadingEditProducts] = useState(false);
    const [fetchedCounts, setFetchedCounts] = useState({});
    const [expandedAccordion, setExpandedAccordion] = useState(null);
    const [accordionProducts, setAccordionProducts] = useState({});
    const [loadingAccordion, setLoadingAccordion] = useState({});
    const [accordionSearch, setAccordionSearch] = useState({});
    const [showAddInput, setShowAddInput] = useState(false);
    const [newSubNameDesktop, setNewSubNameDesktop] = useState('');
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [isSortingSubs, setIsSortingSubs] = useState(false);
    const [accordionSubMeta, setAccordionSubMeta] = useState({});
    const [accordionSortSelect, setAccordionSortSelect] = useState({});
    const [accordionSortSaving, setAccordionSortSaving] = useState({});
    const [modalSortSelect, setModalSortSelect] = useState(null);
    const [modalSubMeta, setModalSubMeta] = useState(null);
    const [modalSortSaving, setModalSortSaving] = useState(false);

    const subcategoryIdsKey = useMemo(
        () =>
            (chipDataSubCollection || [])
                .map((sub) => (typeof sub === 'object' ? sub.id : null))
                .filter(Boolean)
                .join(','),
        [chipDataSubCollection]
    );

    useEffect(() => {
        if (!storeId || !subcategoryIdsKey) return;

        subcategoryIdsKey.split(',').forEach((subId) => {
            collectionsxIdService(storeId, subId)
                .then(({ data }) => {
                    setFetchedCounts((prev) => ({
                        ...prev,
                        [String(subId)]: (data.products || []).length,
                    }));
                })
                .catch(() => {});
        });
    }, [storeId, subcategoryIdsKey]);

    const setSubcategoryProductCount = useCallback((subId, count) => {
        setFetchedCounts((prev) => ({ ...prev, [String(subId)]: count }));
    }, []);

    const getProductCount = (sub) => {
        const subId = typeof sub === 'object' ? String(sub.id) : '';
        if (accordionProducts[subId] !== undefined) {
            return (accordionProducts[subId] || []).length;
        }
        if (fetchedCounts[subId] !== undefined) return fetchedCounts[subId];
        if (!sub || !listDataProduct || !listDataProduct.length) return 0;
        return listDataProduct.filter((p) => {
            const cols = p.collections || [];
            return cols.some((c) => String(c.collection_id ?? c.id) === subId);
        }).length;
    };

    const filteredProducts = useMemo(() => {
        if (!listDataProduct) return [];
        if (!productSearch.trim()) return listDataProduct;
        const q = productSearch.toLowerCase();
        return listDataProduct.filter(p => p.name && p.name.toLowerCase().includes(q));
    }, [listDataProduct, productSearch]);

    const getModalProductsForSort = useCallback(() => {
        const catalog = listDataProduct || [];
        return selectedProducts
            .map((id) => catalog.find((p) => String(p.id) === String(id)))
            .filter(Boolean);
    }, [listDataProduct, selectedProducts]);

    const getOrderedModalProductIds = useCallback(() => {
        const products = getModalProductsForSort();
        if (modalSortSelect != null && products.length > 0) {
            return sortProductsBySelectId(products, modalSortSelect).map((p) => String(p.id));
        }
        return selectedProducts.map((id) => String(id));
    }, [getModalProductsForSort, modalSortSelect, selectedProducts]);

    const handleOpenCreateModal = () => {
        setEditingSubcategory(null);
        setNewSubName('');
        setSelectedProducts([]);
        setProductSearch('');
        setModalSortSelect(null);
        setModalSubMeta(null);
        setModalOpen(true);
    };

    const handleOpenEditModal = (subcategory) => {
        const subName = typeof subcategory === 'object' ? subcategory.name : subcategory;
        const subId = typeof subcategory === 'object' ? subcategory.id : null;

        setEditingSubcategory(subcategory);
        setNewSubName(subName || '');
        setProductSearch('');
        setSelectedProducts([]);
        setModalSortSelect(null);
        setModalSubMeta(null);
        setModalOpen(true);

        if (subId && storeId) {
            setLoadingEditProducts(true);
            collectionsxIdService(storeId, subId)
                .then(({ data }) => {
                    const prods = data.products || [];
                    const enriched = enrichCollectionWithSortPreference(data, storeId);
                    setModalSubMeta(enriched);
                    setModalSortSelect(getSubcategorySortSelectIdFromCollection(enriched));
                    setSelectedProducts(prods.map((p) => p.id ?? p.product_id ?? p));
                })
                .catch(() => {
                    const assigned = (listDataProduct || [])
                        .filter((p) =>
                            (p.collections || []).some(
                                (c) => String(c.collection_id ?? c.id) === String(subId)
                            )
                        )
                        .map((p) => p.id);
                    setSelectedProducts(assigned);
                })
                .finally(() => setLoadingEditProducts(false));
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditingSubcategory(null);
        setModalSortSelect(null);
        setModalSubMeta(null);
    };

    const handleModalProductSortChange = async (event) => {
        const raw = event.target.value;

        if (!editingSubcategory?.id) {
            if (raw === '' || raw === null) {
                setModalSortSelect(null);
                return;
            }
            const selectId = Number(raw);
            if (!isSubcategorySortSelectId(selectId)) return;
            setModalSortSelect(selectId);
            return;
        }

        const subId = editingSubcategory.id;
        setModalSortSaving(true);

        try {
            let subData = modalSubMeta;
            if (!subData?.id) {
                const { data } = await collectionsxIdService(storeId, subId);
                subData = data;
                setModalSubMeta(data);
            }

            const catalog = listDataProduct || [];
            const current =
                (subData.products || []).length > 0
                    ? (subData.products || []).map((p) => {
                          const pid = p.id ?? p.product_id ?? p;
                          if (typeof p === 'object' && p.name != null) return p;
                          return catalog.find((item) => String(item.id) === String(pid)) || p;
                      })
                    : getModalProductsForSort();

            if (raw === '' || raw === null) {
                setModalSortSelect(null);
                await persistSubcategoryProducts(
                    subId,
                    current,
                    { sort_by: 'manual', sort_order: 'asc' },
                    subData
                );
                enqueueSnackbar('Orden por defecto aplicado', { variant: 'success', autoHideDuration: 2500 });
                return;
            }

            const selectId = Number(raw);
            if (!isSubcategorySortSelectId(selectId)) return;

            const sorted = sortProductsBySelectId(current, selectId);
            const { sort_by, sort_order } = getSortConfigFromSelectId(selectId);
            setModalSortSelect(selectId);
            setSelectedProducts(sorted.map((p) => p.id ?? p.product_id ?? p));
            await persistSubcategoryProducts(subId, sorted, { sort_by, sort_order }, subData);
            setSubcategoryProductCount(subId, sorted.length);
            enqueueSnackbar('Orden guardado correctamente', {
                variant: 'success',
                autoHideDuration: 2500,
            });
        } catch (err) {
            enqueueSnackbar('Error al guardar el orden', { variant: 'error', autoHideDuration: 2500 });
        } finally {
            setModalSortSaving(false);
        }
    };

    const handleToggleProduct = (productId) => {
        setSelectedProducts(prev =>
            prev.includes(productId)
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    };

    const handleSaveSubcategory = async () => {
        const name = newSubName.trim();
        if (!name) {
            enqueueSnackbar('Por favor añadir un nombre de subcategoría', {
                variant: 'error',
                autoHideDuration: 2500,
            });
            return;
        }

        setSaving(true);
        try {
            if (editingSubcategory) {
                const subId = editingSubcategory.id;
                const subData = modalSubMeta || editingSubcategory;
                const sortOverrides =
                    modalSortSelect != null
                        ? getSortConfigFromSelectId(modalSortSelect)
                        : { sort_by: 'manual', sort_order: 'asc' };
                const payload = {
                    ...buildCollectionUpdatePayload(
                        { ...subData, name },
                        getOrderedModalProductIds(),
                        sortOverrides
                    ),
                    parent_id: parentId,
                };
                await updateCollectionsService(storeId, payload);
                persistCollectionSortPreference(storeId, subId, payload.sort_by, payload.sort_order);

                setChipDataSubCollection(prev =>
                    prev.map(item => {
                        if (typeof item === 'object' && String(item.id) === String(subId)) {
                            return { ...item, name: name };
                        }
                        return item;
                    })
                );
                setFetchedCounts((prev) => ({ ...prev, [String(subId)]: selectedProducts.length }));
                setAccordionSortSelect((prev) => ({
                    ...prev,
                    [String(subId)]: modalSortSelect,
                }));
                setAccordionSubMeta((prev) => ({
                    ...prev,
                    [subId]: {
                        ...(prev[subId] || subData),
                        name,
                        sort_by: sortOverrides.sort_by,
                        sort_order: sortOverrides.sort_order,
                    },
                }));

                enqueueSnackbar('Subcategoría actualizada exitosamente', {
                    variant: 'success',
                    autoHideDuration: 2500,
                });
            } else {
                const { data: created } = await createSubCollection(storeId, parentId, name);

                if (selectedProducts.length > 0 || modalSortSelect != null) {
                    const sortOverrides =
                        modalSortSelect != null
                            ? getSortConfigFromSelectId(modalSortSelect)
                            : {};
                    await updateCollectionsService(storeId, {
                        ...buildCollectionUpdatePayload(
                            created,
                            getOrderedModalProductIds(),
                            sortOverrides
                        ),
                        parent_id: parentId,
                    });
                }

                let nextSubs;
                setChipDataSubCollection((prev) => {
                    nextSubs = [...prev, { id: created.id, name: created.name }];
                    return nextSubs;
                });
                await persistSubcollectionOrder(storeId, nextSubs);
                setFetchedCounts(prev => ({ ...prev, [String(created.id)]: selectedProducts.length }));

                enqueueSnackbar('Subcategoría creada exitosamente', {
                    variant: 'success',
                    autoHideDuration: 2500,
                });
            }

            setModalOpen(false);
            setEditingSubcategory(null);
        } catch (err) {
            enqueueSnackbar(
                editingSubcategory ? 'Error al actualizar la subcategoría' : 'Error al crear la subcategoría',
                { variant: 'error', autoHideDuration: 2500 }
            );
        } finally {
            setSaving(false);
        }
    };

    const persistSubcategoryProducts = useCallback(
        async (subId, productList, sortOverrides = {}, subDataOverride) => {
            const subData = subDataOverride ?? accordionSubMeta[subId];
            if (!subData?.id) return;
            const orderedList = applySavedSortToProducts(productList, { ...subData, ...sortOverrides });
            const payload = {
                ...buildCollectionUpdatePayload(
                    subData,
                    orderedList.map((p) => String(p.id ?? p.product_id ?? p)),
                    sortOverrides
                ),
                parent_id: parentId,
            };
            await updateCollectionsService(storeId, payload);
            persistCollectionSortPreference(storeId, subId, payload.sort_by, payload.sort_order);
            setAccordionProducts((prev) => ({ ...prev, [subId]: orderedList }));
            setAccordionSubMeta((prev) => ({
                ...prev,
                [subId]: {
                    ...subData,
                    sort_by: payload.sort_by,
                    sort_order: payload.sort_order,
                },
            }));
            setModalSubMeta((prev) => {
                if (prev && String(prev.id) === String(subId)) {
                    return { ...prev, sort_by: payload.sort_by, sort_order: payload.sort_order };
                }
                return prev;
            });
        },
        [accordionSubMeta, parentId, storeId]
    );

    const fetchAccordionProducts = useCallback((subId) => {
        setLoadingAccordion(prev => ({ ...prev, [subId]: true }));
        collectionsxIdService(storeId, subId)
            .then(({ data }) => {
                const subMeta = enrichCollectionWithSortPreference(
                    {
                        ...data,
                        sort_by: data.sort_by ?? data.sortBy,
                        sort_order: data.sort_order ?? data.sortOrder,
                    },
                    storeId
                );
                const prods = applySavedSortToProducts(data.products || [], subMeta);
                setAccordionSubMeta((prev) => ({
                    ...prev,
                    [subId]: subMeta,
                }));
                setAccordionSortSelect((prev) => ({
                    ...prev,
                    [subId]: getSubcategorySortSelectIdFromCollection(data),
                }));
                setAccordionProducts((prev) => ({ ...prev, [subId]: prods }));
                setSubcategoryProductCount(subId, prods.length);
            })
            .catch(() => {
                const fallback = (listDataProduct || []).filter(p =>
                    (p.collections || []).some(c => String(c.collection_id ?? c.id) === String(subId))
                );
                setAccordionProducts((prev) => ({ ...prev, [subId]: fallback }));
                setSubcategoryProductCount(subId, fallback.length);
            })
            .finally(() => {
                setLoadingAccordion(prev => ({ ...prev, [subId]: false }));
            });
    }, [storeId, listDataProduct, setSubcategoryProductCount]);

    const handleSubSortStart = () => {
        setIsSortingSubs(true);
        setExpandedAccordion(null);
    };

    const handleSubSortEnd = ({ oldIndex, newIndex }) => {
        setIsSortingSubs(false);
        if (oldIndex === newIndex) return;
        const previous = chipDataSubCollection;
        const reordered = arrayMove(chipDataSubCollection, oldIndex, newIndex);
        setChipDataSubCollection(reordered);
        const ids = getSubcollectionIds(reordered);
        orderCollections(storeId, ids)
            .then(() => {
                enqueueSnackbar('Orden de subcategorías guardado', {
                    variant: 'success',
                    autoHideDuration: 2500,
                });
            })
            .catch(() => {
                setChipDataSubCollection(previous);
                enqueueSnackbar('Error al guardar el orden', {
                    variant: 'error',
                    autoHideDuration: 2500,
                });
            });
    };

    if (isMobile) {
        return (
            <>
                <Card elevation={0} style={mobileCardStyle}>
                    <CardContent style={{ padding: 16 }}>
                        <Typography
                            variant="subtitle1"
                            style={{ fontWeight: 600, fontSize: 16, marginBottom: 4, color: '#1E1E1E' }}
                        >
                            {t('SubCollection.title')}
                        </Typography>
                        <Typography
                            variant="body2"
                            style={{ color: '#666', fontSize: 13, marginBottom: 16 }}
                        >
                            Organiza subcategorías y el orden de productos (nombre, precio, fecha)
                        </Typography>
                        {chipDataSubCollection.length > 1 && (
                            <Typography
                                variant="caption"
                                style={{ color: '#888', display: 'block', marginBottom: 12 }}
                            >
                                Arrastra para elegir el orden en la tienda
                            </Typography>
                        )}

                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreateModal}
                            style={{
                                borderStyle: 'dashed',
                                borderColor: '#C4C4C4',
                                color: '#333',
                                textTransform: 'none',
                                fontWeight: 500,
                                padding: '10px 16px',
                                borderRadius: 8,
                                marginBottom: 16,
                            }}
                        >
                            Agregar subcategoría
                        </Button>

                        {chipDataSubCollection.length > 0 && (
                            <SortableSubCollectionList
                                {...SUBCOLLECTION_SORTABLE_PROPS}
                                onSortStart={handleSubSortStart}
                                onSortEnd={handleSubSortEnd}
                                getHelperDimensions={getSubcollectionHelperDimensions}
                            >
                                {chipDataSubCollection.map((data, index) => {
                                    const label =
                                        typeof data === 'object' && data && data.name != null
                                            ? data.name
                                            : data;
                                    const key =
                                        typeof data === 'object' && data && data.id != null
                                            ? data.id
                                            : index;
                                    const productCount = typeof data === 'object' ? getProductCount(data) : 0;

                                    return (
                                        <SortableSubCollectionRow key={key} index={index}>
                                            <Box
                                                className="subcollection-sortable-surface"
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    backgroundColor: '#F9F9F9',
                                                    borderRadius: 12,
                                                    padding: '12px',
                                                    marginBottom: 8,
                                                    border: '1px solid #F0F0F0',
                                                }}
                                            >
                                                <SubCollectionDragHandle />
                                                <Box
                                                    style={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: '50%',
                                                        backgroundColor: '#EDF5E6',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        marginRight: 12,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <CategoryIcon style={{ fontSize: 18, color: '#173300' }} />
                                                </Box>
                                                <Box style={{ flex: 1, minWidth: 0 }}>
                                                    <Typography
                                                        variant="body2"
                                                        style={{ fontWeight: 600, fontSize: 14, color: '#1E1E1E' }}
                                                    >
                                                        {label}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        style={{ color: '#888', fontSize: 11 }}
                                                    >
                                                        {productCount} productos
                                                    </Typography>
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenEditModal(data)}
                                                >
                                                    <EditOutlined style={{ fontSize: 18, color: '#2E7D32' }} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={handleDelete(data)}
                                                >
                                                    <DeleteOutline style={{ fontSize: 18, color: '#CB7C7A' }} />
                                                </IconButton>
                                            </Box>
                                        </SortableSubCollectionRow>
                                    );
                                })}
                            </SortableSubCollectionList>
                        )}
                    </CardContent>
                </Card>

                <Dialog
                    open={modalOpen}
                    onClose={handleCloseModal}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{
                        style: {
                            borderRadius: 16,
                            margin: 16,
                            maxHeight: '85vh',
                        },
                    }}
                >
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '16px 20px 8px',
                        }}
                    >
                        <Typography variant="h6" style={{ fontWeight: 600, fontSize: 18 }}>
                            {editingSubcategory ? 'Editar Subcategoría' : 'Crear Subcategoría'}
                        </Typography>
                        <IconButton size="small" onClick={handleCloseModal}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <DialogContent style={{ padding: '8px 20px 16px' }}>
                        <Typography
                            variant="body2"
                            style={{ fontWeight: 500, marginBottom: 8, color: '#333' }}
                        >
                            Nombre de la subcategoría *
                        </Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            placeholder="Ej: Hombre"
                            value={newSubName}
                            onChange={(e) => setNewSubName(e.target.value)}
                            style={{ marginBottom: 20 }}
                        />

                        <SubcategoryProductSortSelect
                            fullWidth
                            value={modalSortSelect ?? ''}
                            onChange={handleModalProductSortChange}
                            disabled={modalSortSaving || loadingEditProducts}
                        />

                        {/* TODO: Sección de íconos - pendiente implementación en backend */}

                        <Box
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: 8,
                            }}
                        >
                            <Typography variant="body2" style={{ fontWeight: 500, color: '#333' }}>
                                Asignar productos
                            </Typography>
                            <Typography variant="caption" style={{ color: '#2E7D32', fontWeight: 500 }}>
                                {selectedProducts.length} seleccionados
                            </Typography>
                        </Box>
                        <Typography
                            variant="caption"
                            style={{ color: '#888', display: 'block', marginBottom: 12 }}
                        >
                            Selecciona los productos que pertenecen a esta subcategoría
                        </Typography>

                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            placeholder="Buscar productos..."
                            value={productSearch}
                            onChange={(e) => setProductSearch(e.target.value)}
                            style={{ marginBottom: 12 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon style={{ color: '#999', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {loadingEditProducts ? (
                            <Box style={{ textAlign: 'center', padding: 20 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <Box style={{ maxHeight: 220, overflowY: 'auto' }}>
                                {filteredProducts.map((product) => {
                                    const isChecked = selectedProducts.includes(product.id);
                                    const imgUrl = getProductImageUrl(product);
                                    const price = product.price
                                        ? `$${Number(product.price).toFixed(2)}`
                                        : '';

                                    return (
                                        <Box
                                            key={product.id}
                                            onClick={() => handleToggleProduct(product.id)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '10px 4px',
                                                borderBottom: '1px solid #F0F0F0',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <Checkbox
                                                checked={isChecked}
                                                size="small"
                                                style={{ color: isChecked ? '#2E7D32' : undefined, padding: 4 }}
                                            />
                                            {imgUrl ? (
                                                <Box
                                                    component="img"
                                                    src={imgUrl}
                                                    alt={product.name}
                                                    style={{
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: 6,
                                                        objectFit: 'cover',
                                                        marginLeft: 8,
                                                        marginRight: 12,
                                                        backgroundColor: '#f5f5f5',
                                                    }}
                                                />
                                            ) : (
                                                <Box
                                                    style={{
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: 6,
                                                        marginLeft: 8,
                                                        marginRight: 12,
                                                        backgroundColor: '#f0f0f0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <CategoryIcon style={{ fontSize: 18, color: '#999' }} />
                                                </Box>
                                            )}
                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    style={{ fontWeight: 500, fontSize: 13, color: '#1E1E1E' }}
                                                >
                                                    {product.name}
                                                </Typography>
                                                {price && (
                                                    <Typography
                                                        variant="caption"
                                                        style={{ color: '#666' }}
                                                    >
                                                        {price}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                                {filteredProducts.length === 0 && (
                                    <Typography
                                        variant="body2"
                                        style={{ textAlign: 'center', padding: 20, color: '#888' }}
                                    >
                                        No se encontraron productos
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </DialogContent>

                    <DialogActions style={{ padding: '12px 20px 20px', gap: 12 }}>
                        <Button
                            onClick={handleCloseModal}
                            variant="outlined"
                            style={{
                                flex: 1,
                                textTransform: 'none',
                                borderRadius: 8,
                                color: '#333',
                                borderColor: '#DDD',
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSaveSubcategory}
                            variant="contained"
                            color="primary"
                            disabled={saving || !newSubName.trim()}
                            style={{
                                flex: 1,
                                textTransform: 'none',
                                borderRadius: 8,
                            }}
                        >
                            {saving ? <CircularProgress size={20} color="inherit" /> : 'Guardar'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }

    const handleAccordionChange = (subId) => (event, isExpanded) => {
        setExpandedAccordion(isExpanded ? subId : null);
        if (isExpanded && !accordionProducts[subId]) {
            fetchAccordionProducts(subId);
        }
    };

    const handleAccordionProductSortChange = async (subId, event) => {
        const raw = event.target.value;
        const current = accordionProducts[subId] || [];
        setAccordionSortSaving((prev) => ({ ...prev, [subId]: true }));

        try {
            let subData = accordionSubMeta[subId];
            if (!subData?.id) {
                const { data } = await collectionsxIdService(storeId, subId);
                subData = data;
                setAccordionSubMeta((prev) => ({ ...prev, [subId]: data }));
            }

            if (raw === '' || raw === null) {
                setAccordionSortSelect((prev) => ({ ...prev, [subId]: null }));
                setAccordionProducts((prev) => ({ ...prev, [subId]: current }));
                await persistSubcategoryProducts(
                    subId,
                    current,
                    { sort_by: 'manual', sort_order: 'asc' },
                    subData
                );
                enqueueSnackbar('Orden por defecto aplicado', { variant: 'success', autoHideDuration: 2500 });
                return;
            }

            const selectId = Number(raw);
            if (!isSubcategorySortSelectId(selectId)) return;

            const sorted = sortProductsBySelectId(current, selectId);
            const { sort_by, sort_order } = getSortConfigFromSelectId(selectId);
            setAccordionSortSelect((prev) => ({ ...prev, [subId]: selectId }));
            setAccordionProducts((prev) => ({ ...prev, [subId]: sorted }));
            await persistSubcategoryProducts(subId, sorted, { sort_by, sort_order }, subData);
            enqueueSnackbar('Orden guardado correctamente', {
                variant: 'success',
                autoHideDuration: 2500,
            });
        } catch (err) {
            fetchAccordionProducts(subId);
            enqueueSnackbar('Error al guardar el orden', { variant: 'error', autoHideDuration: 2500 });
        } finally {
            setAccordionSortSaving((prev) => ({ ...prev, [subId]: false }));
        }
    };

    const handleRemoveProductFromSub = async (subId, productId) => {
        const currentProducts = accordionProducts[subId] || [];
        const nextProducts = currentProducts.filter(p => String(p.id ?? p.product_id ?? p) !== String(productId));
        setAccordionProducts((prev) => ({ ...prev, [subId]: nextProducts }));
        setSubcategoryProductCount(subId, nextProducts.length);

        try {
            await persistSubcategoryProducts(subId, nextProducts);
            if (setListDataProduct) {
                setListDataProduct(list => list.map(p =>
                    String(p.id) === String(productId)
                        ? { ...p, collections: (p.collections || []).filter(c => String(c.collection_id ?? c.id) !== String(subId)) }
                        : p
                ));
            }
            enqueueSnackbar('Producto removido de la subcategoría', { variant: 'success', autoHideDuration: 2500 });
        } catch (err) {
            fetchAccordionProducts(subId);
            enqueueSnackbar('Error al remover producto', { variant: 'error', autoHideDuration: 2500 });
        }
    };

    const handleAddProductToSub = async (subId, product) => {
        const currentProducts = accordionProducts[subId] || [];
        if (currentProducts.some(p => String(p.id ?? p.product_id ?? p) === String(product.id))) {
            return;
        }
        try {
            let subData = accordionSubMeta[subId];
            if (!subData) {
                const { data } = await collectionsxIdService(storeId, subId);
                subData = {
                    ...data,
                    sort_by: data.sort_by ?? data.sortBy,
                    sort_order: data.sort_order ?? data.sortOrder,
                };
                setAccordionSubMeta((prev) => ({ ...prev, [subId]: subData }));
                setAccordionSortSelect((prev) => ({
                    ...prev,
                    [subId]: getSubcategorySortSelectIdFromCollection(data),
                }));
            }
            const nextProducts = addProductWithCollectionSort(currentProducts, product, subData);
            setAccordionProducts((prev) => ({ ...prev, [subId]: nextProducts }));
            setSubcategoryProductCount(subId, nextProducts.length);
            await persistSubcategoryProducts(subId, nextProducts, {}, subData);
            if (setListDataProduct) {
                setListDataProduct(list => list.map(p =>
                    String(p.id) === String(product.id)
                        ? { ...p, collections: [...(p.collections || []), { collection_id: subId }] }
                        : p
                ));
            }
            enqueueSnackbar('Producto agregado a la subcategoría', { variant: 'success', autoHideDuration: 2500 });
        } catch (err) {
            fetchAccordionProducts(subId);
            enqueueSnackbar('Error al agregar producto', { variant: 'error', autoHideDuration: 2500 });
        }
    };

    const handleRenameSubcategory = async (subId) => {
        const name = renameValue.trim();
        if (!name) return;
        try {
            const { data: subData } = await collectionsxIdService(storeId, subId);
            await updateCollectionsService(storeId, {
                id: subId,
                name: name,
                description: subData.description || '',
                public: Number(subData.public) ? 1 : 0,
                parent_id: parentId,
                products: (subData.products || []).map(p => String(p.id ?? p.product_id ?? p)),
            });
            setChipDataSubCollection(prev => prev.map(item =>
                typeof item === 'object' && String(item.id) === String(subId)
                    ? { ...item, name }
                    : item
            ));
            setRenamingId(null);
            enqueueSnackbar('Subcategoría renombrada', { variant: 'success', autoHideDuration: 2500 });
        } catch (err) {
            enqueueSnackbar('Error al renombrar', { variant: 'error', autoHideDuration: 2500 });
        }
    };

    const handleAddSubDesktop = () => {
        const name = newSubNameDesktop.trim();
        if (!name) {
            enqueueSnackbar('Por favor añadir un nombre de subcategoría', { variant: 'error', autoHideDuration: 2500 });
            return;
        }
        createSubCollection(storeId, parentId, name)
            .then(({ data: created }) => {
                let nextSubs;
                setChipDataSubCollection((prev) => {
                    nextSubs = [...prev, { id: created.id, name: created.name }];
                    return nextSubs;
                });
                return persistSubcollectionOrder(storeId, nextSubs).then(() => {
                    setNewSubNameDesktop('');
                    setShowAddInput(false);
                    enqueueSnackbar('Subcategoría creada exitosamente', { variant: 'success', autoHideDuration: 2500 });
                });
            })
            .catch(() => {
                enqueueSnackbar('Error al crear la subcategoría', { variant: 'error', autoHideDuration: 2500 });
            });
    };

    const getFilteredProductsForAccordion = (subId) => {
        const search = (accordionSearch[subId] || '').toLowerCase();
        if (!listDataProduct) return [];
        const assignedIds = new Set((accordionProducts[subId] || []).map(p => String(p.id ?? p.product_id ?? p)));
        const available = listDataProduct.filter(p => !assignedIds.has(String(p.id)));
        if (!search) return available.slice(0, 10);
        return available.filter(p => p.name && p.name.toLowerCase().includes(search)).slice(0, 10);
    };

    return (
        <Card style={{ marginTop: 15 }} elevation={0}>
            <CardHeader
                titleTypographyProps={{ variant: 'h6' }}
                title={t('SubCollection.title')}
                subheader="Organiza subcategorías (arrastrar) y ordena productos dentro de cada una (nombre, precio, fecha)."
                subheaderTypographyProps={{ variant: 'body2', style: { color: '#666', marginTop: 4 } }}
            />
            <CardContent style={{ paddingTop: 0 }}>
                {chipDataSubCollection.length === 0 && !showAddInput && (
                    <Box style={{ textAlign: 'center', padding: '24px 0' }}>
                        <CategoryIcon style={{ fontSize: 40, color: '#ccc', marginBottom: 8 }} />
                        <Typography variant="body2" style={{ color: '#888', marginBottom: 16 }}>
                            No hay subcategorías creadas
                        </Typography>
                    </Box>
                )}

                <SortableSubCollectionList
                    {...SUBCOLLECTION_SORTABLE_PROPS}
                    onSortStart={handleSubSortStart}
                    onSortEnd={handleSubSortEnd}
                    getHelperDimensions={getSubcollectionHelperDimensions}
                >
                {chipDataSubCollection.map((data, index) => {
                    const subId = typeof data === 'object' ? data.id : null;
                    const subName = typeof data === 'object' ? data.name : data;
                    if (!subId) return null;
                    const products = accordionProducts[subId] || [];
                    const productCount = getProductCount(data);
                    const isLoading = loadingAccordion[subId];
                    const isRenaming = renamingId === subId;

                    return (
                        <SortableSubCollectionRow key={subId} index={index}>
                        <ExpansionPanel
                            className="subcollection-sortable-surface"
                            expanded={!isSortingSubs && expandedAccordion === subId}
                            onChange={handleAccordionChange(subId)}
                            style={{ marginBottom: 8, borderRadius: 8, border: '1px solid #E8E8E8', boxShadow: 'none' }}
                            TransitionProps={{ unmountOnExit: true, timeout: isSortingSubs ? 0 : undefined }}
                        >
                            <ExpansionPanelSummary
                                expandIcon={<ExpandMoreIcon />}
                                style={{ minHeight: 52 }}
                            >
                                <SubCollectionDragHandle />
                                <Box style={{ display: 'flex', alignItems: 'center', flex: 1, gap: 12 }}>
                                    {isRenaming ? (
                                        <TextField
                                            size="small"
                                            variant="outlined"
                                            value={renameValue}
                                            onChange={(e) => setRenameValue(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') { e.preventDefault(); handleRenameSubcategory(subId); }
                                                if (e.key === 'Escape') setRenamingId(null);
                                            }}
                                            autoFocus
                                            style={{ maxWidth: 200 }}
                                        />
                                    ) : (
                                        <Typography style={{ fontWeight: 600, fontSize: 15 }}>
                                            {subName}
                                        </Typography>
                                    )}
                                    <Box
                                        style={{
                                            backgroundColor: '#E8F5E9',
                                            color: '#2E7D32',
                                            borderRadius: 12,
                                            padding: '2px 10px',
                                            fontSize: 12,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {productCount} productos
                                    </Box>
                                </Box>
                                <Box onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 4 }}>
                                    {isRenaming ? (
                                        <>
                                            <Button size="small" onClick={() => handleRenameSubcategory(subId)} style={{ textTransform: 'none', fontSize: 12 }}>
                                                Guardar
                                            </Button>
                                            <Button size="small" onClick={() => setRenamingId(null)} style={{ textTransform: 'none', fontSize: 12 }}>
                                                Cancelar
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <IconButton size="small" onClick={() => { setRenamingId(subId); setRenameValue(subName); }}>
                                                <EditOutlined style={{ fontSize: 18 }} />
                                            </IconButton>
                                            <IconButton size="small" onClick={handleDelete(data)}>
                                                <DeleteOutline style={{ fontSize: 18, color: '#CB7C7A' }} />
                                            </IconButton>
                                        </>
                                    )}
                                </Box>
                            </ExpansionPanelSummary>
                            <ExpansionPanelDetails style={{ flexDirection: 'column', padding: '0 16px 16px' }}>
                                {isLoading ? (
                                    <Box style={{ textAlign: 'center', padding: 16 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : (
                                    <>
                                        <SubcategoryProductSortSelect
                                            value={accordionSortSelect[subId] ?? ''}
                                            onChange={(e) => handleAccordionProductSortChange(subId, e)}
                                            disabled={Boolean(accordionSortSaving[subId])}
                                        />
                                        {products.length > 0 && (
                                            <Box style={{ marginBottom: 12 }}>
                                                {products.map((product) => {
                                                    const pid = product.id ?? product.product_id ?? product;
                                                    const pName = product.name || '';
                                                    const imgUrl = getProductImageUrl(product);
                                                    return (
                                                        <Box
                                                            key={pid}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                padding: '8px 0',
                                                                borderBottom: '1px solid #F5F5F5',
                                                            }}
                                                        >
                                                            {imgUrl ? (
                                                                <Box
                                                                    component="img"
                                                                    src={imgUrl}
                                                                    alt={pName}
                                                                    style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover', marginRight: 12, backgroundColor: '#f5f5f5' }}
                                                                />
                                                            ) : (
                                                                <Box style={{ width: 36, height: 36, borderRadius: 6, marginRight: 12, backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    <CategoryIcon style={{ fontSize: 16, color: '#bbb' }} />
                                                                </Box>
                                                            )}
                                                            <Typography variant="body2" style={{ flex: 1, fontSize: 14 }}>
                                                                {pName}
                                                            </Typography>
                                                            <IconButton size="small" onClick={() => handleRemoveProductFromSub(subId, pid)}>
                                                                <CloseIcon style={{ fontSize: 16, color: '#999' }} />
                                                            </IconButton>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        )}
                                        <Box style={{ position: 'relative' }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                placeholder="Buscar producto para agregar..."
                                                value={accordionSearch[subId] || ''}
                                                onChange={(e) => { const val = e.target.value; setAccordionSearch(prev => ({ ...prev, [subId]: val })); }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon style={{ color: '#999', fontSize: 18 }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            {(accordionSearch[subId] || '').trim() && (
                                                <Box style={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: 0,
                                                    right: 0,
                                                    zIndex: 5,
                                                    backgroundColor: '#fff',
                                                    border: '1px solid #E8E8E8',
                                                    borderRadius: 8,
                                                    maxHeight: 200,
                                                    overflowY: 'auto',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                                }}>
                                                    {getFilteredProductsForAccordion(subId).map(product => {
                                                        const imgUrl = getProductImageUrl(product);
                                                        return (
                                                            <Box
                                                                key={product.id}
                                                                onClick={() => {
                                                                    handleAddProductToSub(subId, product);
                                                                    setAccordionSearch(prev => ({ ...prev, [subId]: '' }));
                                                                }}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    padding: '8px 12px',
                                                                    cursor: 'pointer',
                                                                    borderBottom: '1px solid #F5F5F5',
                                                                }}
                                                            >
                                                                {imgUrl ? (
                                                                    <Box component="img" src={imgUrl} alt={product.name} style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover', marginRight: 10 }} />
                                                                ) : (
                                                                    <Box style={{ width: 32, height: 32, borderRadius: 4, marginRight: 10, backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                        <CategoryIcon style={{ fontSize: 14, color: '#bbb' }} />
                                                                    </Box>
                                                                )}
                                                                <Typography variant="body2" style={{ fontSize: 13 }}>
                                                                    {product.name}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })}
                                                    {getFilteredProductsForAccordion(subId).length === 0 && (
                                                        <Typography variant="body2" style={{ padding: 12, color: '#888', textAlign: 'center' }}>
                                                            No se encontraron productos
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </>
                                )}
                            </ExpansionPanelDetails>
                        </ExpansionPanel>
                        </SortableSubCollectionRow>
                                );
                            })}
                </SortableSubCollectionList>

                {showAddInput ? (
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Nombre de la subcategoría"
                            value={newSubNameDesktop}
                            onChange={(e) => setNewSubNameDesktop(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); handleAddSubDesktop(); }
                                if (e.key === 'Escape') { setShowAddInput(false); setNewSubNameDesktop(''); }
                            }}
                            autoFocus
                            style={{ flex: 1 }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={handleAddSubDesktop}
                            style={{ textTransform: 'none' }}
                        >
                            Agregar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => { setShowAddInput(false); setNewSubNameDesktop(''); }}
                            style={{ textTransform: 'none' }}
                        >
                            Cancelar
                        </Button>
                    </Box>
                ) : (
                    <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setShowAddInput(true)}
                        style={{
                            marginTop: 12,
                            borderStyle: 'dashed',
                            borderColor: '#C4C4C4',
                            color: '#555',
                            textTransform: 'none',
                            fontWeight: 500,
                            padding: '10px 16px',
                            borderRadius: 8,
                        }}
                    >
                        Agregar subcategoría
                    </Button>
                )}
            </CardContent>
        </Card>
    );
};
export default connect()(SubCollection);
